import { CloseCircleOutlined, LeftCircleOutlined } from '@ant-design/icons';
import { Collapse, List, message, Tooltip } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { isDefined } from 'class-validator';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { convertInterestListToBlockEditData } from 'src/app/components/NewsInfoComponent/news-info.config';
import OptionTags from 'src/app/components/OptionTagsComponent/OptionTags';
import SearchableSelect from 'src/app/components/SearchableSelect/SearchableSelect';
import { getEnumKeyByValue, getEnumSelectOptions } from 'src/app/helpers/enum.helper';
import { convertInterestListToSelectOptions } from 'src/app/helpers/service.helper';
import { EditBlockDataType } from 'src/app/redux/block/block-slice.type';
import { useAppSelector } from 'src/app/redux/hook';
import { useGetAllInterestsQuery } from 'src/app/redux/interests/interestsSlice';
import { InterestsWithNames } from 'src/app/services/interest/interest.service.type';
import {
  deleteBlockFeedback,
  getBlockStatisticStates,
  giveBlockFeedback,
  updateBlockFeedback,
} from 'src/app/services/surfer-service/surfer.service';
import { BlockResultType, BlockStatistic } from 'src/app/services/surfer-service/surfer.service.type';
import { INTERESTS_WHITELIST } from 'src/configs/system.config';
import { InterestClassType, InterestsTypes } from 'src/enums/interests.enum';
import { BlockStatisticStatus } from 'src/enums/surfer.enums';
import { DeleteIcon, EditIcon } from 'src/icons';
import AddInteractionIcon from 'src/icons/AddInteractionIcon';
import { IdValuePairType } from 'src/types/interests.type';

interface IBlockStatisticProps {
  blockCategories: BlockResultType['interestList'];
  blockInterests: BlockResultType['interestList'];
  blockId: string;
  selectedInterestNames: DefaultOptionType[] | undefined;
  setSelectedInterestNames: React.Dispatch<React.SetStateAction<DefaultOptionType[]>>;
  setEditBlockInterests: React.Dispatch<React.SetStateAction<{ [key: string]: EditBlockDataType[number] }>>;
  editBlockInterests: { [key: string]: EditBlockDataType[number] };
}

const BlockStatisticList: React.FC<IBlockStatisticProps> = ({
  blockCategories,
  blockInterests,
  blockId,
  setSelectedInterestNames,
  selectedInterestNames,
  setEditBlockInterests,
  editBlockInterests,
}) => {
  const [blockStatistics, setBlockStatistics] = useState<BlockStatistic[]>([]);
  const [addMode, setAddMode] = useState(false);
  const [selectedInterestType, setSelectedInterestType] = useState<InterestsTypes>();
  const [blockInterestsCopy, setBlockInterestsCopy] = useState([...blockCategories]);
  // const [selectedInterestNames, setSelectedInterestNames] = useState<DefaultOptionType[]>();

  useEffect(() => {
    setEditBlockInterests(convertInterestListToBlockEditData(blockInterests));
  }, []);

  const { data: interestNames } = useGetAllInterestsQuery(
    {
      types: [selectedInterestType!],

      order: 'name',
      customerStatus: selectedInterestType === InterestsTypes.Enterprise ? true : undefined,
      interestsClassTypes: InterestClassType.Identified,
      isProxy: selectedInterestType === InterestsTypes.Enterprise ? false : undefined,
    },
    {
      skip: !isDefined(selectedInterestType),
    },
  );

  /*  This function is used to get interest list from given blockInterests prop
      It takes data as parameter which refers to all interest list
  */
  const filterInterestList = (data: InterestsWithNames[]) => {
    const result: InterestsWithNames[] = [];
    let temp: IdValuePairType[] = [];
    data.forEach((obj) => {
      blockInterestsCopy.forEach((block) => {
        if (block.type === obj.interestType) {
          block.interestId.forEach((int) => {
            const foundInterest = obj.interests.find((x) => x.id === int.id);
            if (foundInterest) temp.push(foundInterest);
          });
          if (temp.length) {
            result.push({
              interestType: obj.interestType,
              interests: [...temp],
            });
            temp = [];
          }
        }
      });
    });

    return result;
  };

  const { data: interestList } = useGetAllInterestsQuery(
    { customerStatus: true },
    {
      selectFromResult: ({ data }) => ({
        data: filterInterestList(data?.rows || []),
      }),
    },
  );

  // Get all block statistic data on initial render
  useEffect(() => {
    const getBlockStatistics = async () => {
      try {
        const { data } = await getBlockStatisticStates();
        setBlockStatistics(data.rows);
      } catch (error) {
        console.log('error', error);
      }
    };

    getBlockStatistics();
  }, []);

  /*
  Three condition is applied in this function.
  First -> If no block is found with given interest id, create new block statistic entity.
  Second -> If block is found but different block statistic is clicked, then update that block statistic.
  Third -> If block is found and same block statistic is clicked, then delete that block statistic.
  */
  const updateBlockState = async (status: BlockStatisticStatus, interestId: string) => {
    try {
      const foundBlock = blockStatistics.find((obj) => obj.blockId === blockId && obj.interestId === interestId);
      if (!foundBlock) {
        const { data } = await giveBlockFeedback({
          blockId,
          status,
          interestId,
        });
        setBlockStatistics((prev) => [...prev, data]);
        message.success('Feedback saved successfully');
      } else if (foundBlock.status !== status) {
        const { data } = await updateBlockFeedback(foundBlock.id, status);
        const index = blockStatistics.findIndex((obj) => obj.blockId === blockId && obj.interestId === interestId);
        setBlockStatistics((prev) => [...prev.slice(0, index), data, ...prev.slice(index + 1)]);
        message.success('Feedback updated successfully');
      } else {
        await deleteBlockFeedback(foundBlock.id);
        setBlockStatistics((prev) => prev.filter((old) => old.id !== foundBlock.id));
        message.success('Feedback updated successfully');
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const interestNameOptions: DefaultOptionType[] = useMemo(() => {
    return convertInterestListToSelectOptions(interestNames?.rows || []).map((opt) => {
      const tempOpt = { ...opt };

      opt.options.forEach((subOpt: DefaultOptionType) => {
        interestList.forEach((int) => {
          int.interests.forEach((i) => {
            if (subOpt.value === i.id) {
              subOpt.disabled = true;
            }
          });
        });
      });
      return tempOpt;
    });
  }, [interestNames, interestList]);

  const { theme } = useAppSelector((state) => state.configContent);

  return (
    <div style={{ overflow: 'auto', width: '100%', height: '100%' }}>
      <div style={{ marginLeft: 'auto', width: 'fit-content', paddingRight: '1rem' }}>
        <Tooltip title="Add New Interest">
          <div>
            <AddInteractionIcon
              color={theme.colors.quoteBgc}
              style={{ width: '25px', height: '25px' }}
              onClick={() => setAddMode(true)}
            />
          </div>
        </Tooltip>
      </div>
      <div style={{ paddingTop: '1rem' }}>
        {blockInterestsCopy.map((obj) => {
          return (
            <div key={obj.type} style={{ marginTop: '.5rem' }} id="block-feedback-collapse">
              <Collapse
                key={obj.type}
                items={[
                  {
                    label: getEnumKeyByValue(InterestsTypes, obj.type),
                    children: (
                      <List
                        style={{
                          maxHeight: '30vh',
                          overflowY: 'auto',
                          paddingInline: '1rem',
                        }}
                        dataSource={interestList}
                        renderItem={({ interestType, interests }) => {
                          if (interestType !== obj.type) return null;
                          return interests.map((int) => {
                            const foundStatus = blockStatistics.find(
                              (block) => block.blockId === blockId && block.interestId === int.id,
                            )?.status;

                            return (
                              <ListItem
                                key={int.id}
                                int={int}
                                setSelectedInterestNames={setSelectedInterestNames}
                                editBlockInterests={editBlockInterests}
                                setEditBlockInterests={setEditBlockInterests}
                                blockInterestsCopy={blockInterestsCopy}
                                setBlockInterestsCopy={setBlockInterestsCopy}
                              />
                            );
                          });
                        }}
                      />
                    ),
                  },
                ]}
              />
            </div>
          );
        })}
      </div>
      {addMode ? (
        <div style={{ marginTop: '.5rem' }} id="block-feedback-collapse">
          <Collapse
            activeKey="1"
            items={[
              {
                key: '1',
                extra: (
                  <div style={{ display: 'flex', columnGap: '1rem' }}>
                    {/* <AiOutlineCheck onClick={onAddInterestSave} /> */}
                    <CloseCircleOutlined
                      onClick={() => {
                        setSelectedInterestNames([]);
                        setAddMode(false);
                      }}
                    />
                  </div>
                ),
                label: (
                  <SearchableSelect
                    placeholder="Select Interest Type"
                    variant="borderless"
                    style={{ width: '200px' }}
                    options={getEnumSelectOptions(InterestsTypes, INTERESTS_WHITELIST())}
                    onChange={(value) => setSelectedInterestType(value)}
                  />
                ),
                children: (
                  <>
                    <SearchableSelect
                      placeholder="Select Interest"
                      labelInValue
                      mode="multiple"
                      value={selectedInterestNames}
                      variant="borderless"
                      style={{ width: '200px' }}
                      options={interestNameOptions}
                      onChange={(value) => {
                        setSelectedInterestNames(value);
                      }}
                      // eslint-disable-next-line react/jsx-no-useless-fragment
                      tagRender={() => <></>}
                    />
                    {selectedInterestNames ? (
                      <OptionTags
                        onDelete={(filtered) => setSelectedInterestNames(filtered)}
                        options={selectedInterestNames}
                      />
                    ) : null}
                  </>
                ),
              },
            ]}
          />
        </div>
      ) : null}
    </div>
  );
};

const StatusIcon = ({ children, tooltipTitle }: { children: ReactElement<any>; tooltipTitle: string }) => {
  return (
    <Tooltip title={tooltipTitle}>
      {React.cloneElement(children, {
        size: 22,
        // instead of pure black we have added
        // primary text color here so on dark theme this will look much better...
        style: { color: 'var(--primary_text_color)', cursor: 'pointer' },
      })}
    </Tooltip>
  );
};

const ListItem = ({
  int,
  setSelectedInterestNames,
  editBlockInterests,
  setEditBlockInterests,
  blockInterestsCopy,
  setBlockInterestsCopy,
}: {
  int: IdValuePairType;
  editBlockInterests: { [key: string]: EditBlockDataType[number] };
  setEditBlockInterests: React.Dispatch<React.SetStateAction<{ [key: string]: EditBlockDataType[number] }>>;
  setSelectedInterestNames: React.Dispatch<React.SetStateAction<DefaultOptionType[]>>;
  blockInterestsCopy: BlockResultType['interestList'];
  setBlockInterestsCopy: React.Dispatch<React.SetStateAction<BlockResultType['interestList']>>;
}) => {
  const [editMode, setEditMode] = useState(false);
  const [value, setValue] = useState<DefaultOptionType>({ value: int.id, label: int.value });
  const [selectedInterestType, setSelectedInterestType] = useState<InterestsTypes>();
  const [showPreviousBtn, setShowPreviousBtn] = useState(false);

  const { theme } = useAppSelector((state) => state.configContent);

  const { data: interestList } = useGetAllInterestsQuery(
    {
      types: [selectedInterestType!],

      order: 'name',
      customerStatus: selectedInterestType === InterestsTypes.Enterprise ? true : undefined,
      interestsClassTypes: InterestClassType.Identified,
      isProxy: selectedInterestType === InterestsTypes.Enterprise ? false : undefined,
    },
    {
      skip: !isDefined(selectedInterestType),
    },
  );

  // const onSave = () => {
  //   console.log('value', value);
  // };

  // const action = (
  //   <div style={{ display: 'flex', columnGap: '1rem' }}>
  //     {editMode ? (
  //       <>
  //         <Button style={{ background: theme.colors.quoteBgc }} onClick={onSave}>
  //           Save
  //         </Button>
  //         <Button
  //           style={{ background: theme.colors.quoteBgc }}
  //           onClick={() => {
  //             setEditMode(false);
  //             setValue(int.value);
  //           }}
  //         >
  //           Cancel
  //         </Button>
  //       </>
  //     ) : (
  //       <EditIcon
  //         onClick={() => {
  //           setEditMode(!editMode);
  //           setSelectedInterestType(int.type);
  //         }}
  //       />
  //     )}
  //   </div>
  // );
  const actionsArr = [
    showPreviousBtn ? (
      <div key={int.id} style={{ display: 'flex', alignItems: 'center' }}>
        <LeftCircleOutlined
          onClick={() => {
            if (!value.value) return;
            const newList = { ...editBlockInterests };
            delete newList[value.value];
            newList[int.id] = { id: int.id, interestClassType: int.classType, type: int.type };
            setEditBlockInterests(newList);
            setValue({ label: int.value, value: int.id });
            setEditMode(false);
            setShowPreviousBtn(false);
          }}
          style={{ fontSize: '20px', color: 'var(--primary_text_color)' }}
        />
      </div>
    ) : null,
    int.classType === InterestClassType.Identified && (
      <EditIcon
        key={int.id}
        onClick={() => {
          setEditMode(!editMode);
          setSelectedInterestType(int.type);
        }}
      />
    ),
    int.classType === InterestClassType.Identified && (
      <DeleteIcon
        key={int.id}
        onClick={() => {
          const foundIndex = blockInterestsCopy.findIndex((i) => i.type === int.type);
          const foundType: BlockResultType['interestList'][number] | undefined = blockInterestsCopy.find(
            (i) => i.type === int.type,
          );
          if (!foundType) return;

          const filtered = foundType?.interestId.filter((i) => i.id !== int.id);
          setBlockInterestsCopy((prev) => [
            ...prev.slice(0, foundIndex),
            { ...foundType, interestId: filtered },
            ...prev.slice(foundIndex + 1),
          ]);
          setEditBlockInterests((prev) => {
            delete prev[int.id];
            return { ...prev };
          });
        }}
      />
    ),
    // action,
    // foundStatus === BlockStatisticStatus.IRRELEVANT ? (
    //   <StatusIcon tooltipTitle="Irrelevant">
    //     <HiMinusCircle
    //       onClick={() => updateBlockState(BlockStatisticStatus.IRRELEVANT, int.id)}
    //     />
    //   </StatusIcon>
    // ) : (
    //   <StatusIcon tooltipTitle="Irrelevant">
    //     <HiOutlineMinusCircle
    //       onClick={() => updateBlockState(BlockStatisticStatus.IRRELEVANT, int.id)}
    //     />
    //   </StatusIcon>
    // ),
    // foundStatus === BlockStatisticStatus.NEGATIVE ? (
    //   <StatusIcon tooltipTitle="Negative">
    //     <BiSolidDislike
    //       onClick={() => updateBlockState(BlockStatisticStatus.NEGATIVE, int.id)}
    //     />
    //   </StatusIcon>
    // ) : (
    //   <StatusIcon tooltipTitle="Negative">
    //     <BiDislike
    //       onClick={() => updateBlockState(BlockStatisticStatus.NEGATIVE, int.id)}
    //     />
    //   </StatusIcon>
    // ),
    // foundStatus === BlockStatisticStatus.POSITIVE ? (
    //   <StatusIcon tooltipTitle="Positive">
    //     <AiFillLike
    //       onClick={() => updateBlockState(BlockStatisticStatus.POSITIVE, int.id)}
    //     />
    //   </StatusIcon>
    // ) : (
    //   <StatusIcon tooltipTitle="Positive">
    //     <AiOutlineLike
    //       onClick={() => updateBlockState(BlockStatisticStatus.POSITIVE, int.id)}
    //     />
    //   </StatusIcon>
    // ),
  ];
  return (
    <List.Item key={int.id} actions={actionsArr.filter((action) => action)}>
      {editMode ? (
        <SearchableSelect
          labelInValue
          style={{ width: '200px' }}
          variant="borderless"
          value={value}
          options={convertInterestListToSelectOptions(interestList?.rows || [])}
          onChange={(val) => {
            let foundInterest: IdValuePairType | null = null;
            interestList?.rows.forEach((obj) => {
              obj.interests.forEach((interest) => {
                if (interest.id === val.value) {
                  foundInterest = interest;
                }
              });
            });

            if (foundInterest === null) return;
            setEditBlockInterests((prev) => {
              delete prev[value.value!];
              return {
                ...prev,
                [foundInterest!.id]: {
                  id: foundInterest!.id,
                  type: foundInterest!.type,
                  interestClassType: foundInterest!.classType,
                },
              };
            });
            setValue(val);
            setShowPreviousBtn(true);
          }}
        />
      ) : (
        int.value
      )}
    </List.Item>
  );
};

export default BlockStatisticList;
